import React, { useState } from 'react';
import { usePhone } from '../../contexts/PhoneContext';
import { PhoneXMarkIcon, MicrophoneIcon, PhoneIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS, Z_INDEX } from '../../design/tokens';

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const statusLabels: Record<string, string> = {
  connecting: 'Connecting...',
  ringing: 'Ringing...',
  active: 'In Call',
  ended: 'Call Ended',
  failed: 'Call Failed',
};

const CallOverlay: React.FC = () => {
  const { callState, isMuted, callDuration, currentNumber, isReady, statusMessage, makeCall, hangup, toggleMute } = usePhone();
  const [isOpen, setIsOpen] = useState(false);
  const [dialNumber, setDialNumber] = useState('');

  const inCall = callState !== 'idle';

  // Normalize to E.164. Only assume +1 (US/NANP) for plain domestic-looking
  // numbers — never blindly prepend +1 to numbers that already carry a country
  // code (e.g. a French number like +33/0033/33...), which produced a wrong number.
  const normalizePhone = (raw: string): string => {
    const trimmed = raw.trim();
    if (trimmed.startsWith('+')) return '+' + trimmed.slice(1).replace(/\D/g, '');
    if (trimmed.startsWith('00')) return '+' + trimmed.slice(2).replace(/\D/g, ''); // 00 = intl access prefix
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 10 && !digits.startsWith('0')) return `+1${digits}`; // US 10-digit
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;    // US 1XXXXXXXXXX
    return `+${digits}`; // already includes a country code (or non-US) — don't force +1
  };

  const handleDial = () => {
    if (!dialNumber.trim()) return;
    makeCall(normalizePhone(dialNumber));
    setDialNumber('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleDial();
  };

  // Floating phone button — positioned left of chat FAB and quick-entry FAB
  // Layout: [Phone 56px] [12px gap] [Chat 56px] [12px gap] [QuickEntry 56px] [24px margin]
  const fabRight = `calc(${SPACING[24]} + 56px + ${SPACING[12]} + 56px + ${SPACING[12]})`;

  if (!isOpen && !inCall) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          bottom: SPACING[24],
          right: fabRight,
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: isReady ? COLORS.status.success : COLORS.text.tertiary,
          border: 'none',
          cursor: isReady ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: Z_INDEX.dropdown,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          transition: 'transform 150ms ease',
        }}
        title={isReady ? 'Open dialer' : (statusMessage || 'Phone connecting...')}
        onMouseEnter={(e) => { if (isReady) e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <PhoneIcon style={{ width: '28px', height: '28px', color: '#FFFFFF' }} />
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: `calc(${SPACING[24]} + 56px + ${SPACING[12]})`,
      right: SPACING[12],
      left: SPACING[12],
      maxWidth: '300px',
      marginLeft: 'auto',
      backgroundColor: COLORS.bg.panel,
      border: `1px solid ${COLORS.border.default}`,
      borderRadius: RADIUS,
      padding: SPACING[16],
      zIndex: Z_INDEX.toast,
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING[12],
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        {inCall ? (
          <span style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: callState === 'failed' ? COLORS.status.error : COLORS.status.success,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            {statusLabels[callState] || callState}
          </span>
        ) : (
          <span style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Dialer
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          {callState === 'active' && (
            <span style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.primary,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {formatDuration(callDuration)}
            </span>
          )}
          {!inCall && (
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: SPACING[4],
                color: COLORS.text.tertiary,
                display: 'flex',
              }}
            >
              <XMarkIcon style={{ width: '16px', height: '16px' }} />
            </button>
          )}
        </div>
      </div>

      {/* In-call: show number */}
      {inCall && (
        <div style={{
          fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.default,
          color: COLORS.text.primary,
        }}>
          {currentNumber}
        </div>
      )}

      {/* Dialer input (when idle) */}
      {!inCall && (
        <div style={{ display: 'flex', gap: SPACING[8] }}>
          <input
            type="tel"
            value={dialNumber}
            onChange={(e) => setDialNumber(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="+1 (555) 123-4567"
            style={{
              flex: 1,
              padding: `${SPACING[8]} ${SPACING[12]}`,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.primary,
              backgroundColor: COLORS.bg.base,
              outline: 'none',
            }}
            autoFocus
          />
          <button
            onClick={handleDial}
            disabled={!dialNumber.trim() || !isReady}
            style={{
              padding: `${SPACING[8]} ${SPACING[12]}`,
              backgroundColor: dialNumber.trim() && isReady ? COLORS.status.success : COLORS.text.tertiary,
              border: 'none',
              borderRadius: RADIUS,
              cursor: dialNumber.trim() && isReady ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              color: '#FFFFFF',
            }}
          >
            <PhoneIcon style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      )}

      {/* Status message */}
      {statusMessage && (
        <div style={{
          fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.small,
          color: callState === 'failed' ? COLORS.status.error : COLORS.text.secondary,
          padding: `${SPACING[4]} 0`,
        }}>
          {statusMessage}
        </div>
      )}

      {/* In-call controls */}
      {(callState === 'connecting' || callState === 'ringing' || callState === 'active') && (
        <div style={{ display: 'flex', gap: SPACING[8] }}>
          <button
            onClick={toggleMute}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: SPACING[4],
              padding: `${SPACING[8]} ${SPACING[12]}`,
              backgroundColor: isMuted ? COLORS.status.warning : COLORS.bg.hover,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              cursor: 'pointer',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              color: COLORS.text.primary,
            }}
          >
            <MicrophoneIcon style={{ width: '16px', height: '16px' }} />
            {isMuted ? 'Unmute' : 'Mute'}
          </button>
          <button
            onClick={hangup}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: SPACING[4],
              padding: `${SPACING[8]} ${SPACING[12]}`,
              backgroundColor: COLORS.status.error,
              border: 'none',
              borderRadius: RADIUS,
              cursor: 'pointer',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              color: '#FFFFFF',
            }}
          >
            <PhoneXMarkIcon style={{ width: '16px', height: '16px' }} />
            Hang Up
          </button>
        </div>
      )}
    </div>
  );
};

export default CallOverlay;
